import type { GeneralObject } from '~/types/CommonTypes';

import {
  AdaptSalesCommissionFilters,
  AdaptSalesCommissionListItem,
} from '~adapters/CommissionsAdapter';
import { MySsApiResponseDataList } from '~/types/MySsApiResponse';
import {
  SalesCommission,
  SalesCommissionFilters,
  SalesCommissionListItem,
} from '~/models/Commission';
import { formatToBrCurrency } from '@scansource-brazil/zeus-core-ui/string';
import {
  listCommissions,
  listCommissionsTotalizers,
  showCommission,
} from '~/services/apiCompany';
import { useAuthStore } from '~/stores/useAuthStore';
import { useTimer } from '~/composables/useTimer';

interface CommissionsTotalizer {
  title: string;
  value: string;
  color: string;
  border: string;
  icon: string;
  itemKey: string;
}

export const useCommissionsStore = defineStore(
  'MY_SCAN_SOURCE_COMMISSIONS_STORE',
  () => {
    const AUTH_STORE = useAuthStore();
    const API = { listCommissions, showCommission };
    const PROFILE_STORE = useProfileStore();

    const { profile } = storeToRefs(PROFILE_STORE);

    const totalizerTimer = useTimer();
    const listTimer = useTimer();

    const commissionListPaginator = usePaginator();

    const commission = ref<GeneralObject>(new SalesCommission());
    const commissionLoading = ref<boolean>(false);
    const commissionList = ref<GeneralObject[]>([]);
    const commissionListLoading = ref<boolean>(false);
    const commissionTotalizers = ref<CommissionsTotalizer[]>([]);
    const commissionTotalizersLoading = ref<boolean>(false);
    const commissionListFilters = ref<SalesCommissionFilters>(
      new SalesCommissionFilters(),
    );

    const getCommission = computed(() => commission.value);
    const getCommissionList = computed(() => commissionList.value);
    const getCommissionListFilters = computed(() => commissionListFilters.value);
    const getCommissionListLoading = computed(() => commissionListLoading.value);
    const getCommissionLoading = computed(() => commissionLoading.value);
    const getCommissionTotalizers = computed(() => commissionTotalizers.value);

    function toggleCommissionListLoading() {
      commissionListLoading.value = !commissionListLoading.value;
    }
    function toggleCommissionLoading() {
      commissionLoading.value = !commissionLoading.value;
    }
    function toggleTotalizerLoading() {
      commissionTotalizersLoading.value = !commissionTotalizersLoading.value;
    }
    function setCommission(content: SalesCommission) {
      commission.value = content;
    }
    function updateCommission(content: SalesCommission) {
      commission.value = { ...commission.value, ...content };
    }
    function clearCommission() {
      commission.value = {};
    }
    function setCommissionList(content: SalesCommissionListItem[] = []) {
      commissionList.value = content;
    }
    function updateCommissionList(content: SalesCommissionListItem[]) {
      commissionList.value = [...commissionList.value, ...content];
    }
    function clearCommissionList() {
      commissionList.value = [];
    }
    function setCommissionTotalizers(totalizers: GeneralObject) {
      commissionTotalizers.value = [
        {
          itemKey: 'commissionAmount',
          title: 'COMMISSIONS.TOTALIZERS.TOTAL',
          value: totalizers.commissionAmount,
          color: 'text-primary',
          border: 'border-primary',
          icon: ICONS.COMMISSIONS,
        },
        {
          itemKey: 'totalAmountReceivable',
          title: 'COMMISSIONS.TOTALIZERS.RECEIVABLE',
          value: totalizers.totalAmountReceivable,
          color: ' text-warning',
          border: 'border-warning',
          icon: ICONS.PAYMENTS,
        },
        {
          itemKey: 'totalAvailableRedemption',
          title: 'COMMISSIONS.TOTALIZERS.AVAILABLE_REDEMPTION',
          value: totalizers.totalAvailableRedemption,
          color: ' text-success',
          border: 'border-success',
          icon: ICONS.CREDIT_CARD,
        },
      ];
    }
    function clearCommissionTotalizers() {
      commissionTotalizers.value = [];
    }
    function setCommissionListFilters(content: SalesCommissionFilters) {
      commissionListFilters.value = content;
    }
    function updateCommissionListFilters(content: SalesCommissionFilters) {
      commissionListFilters.value = {
        ...commissionListFilters.value,
        ...content,
      };
    }

    function clearCommissionListFilters() {
      commissionListFilters.value = new SalesCommissionFilters();
      commissionListPaginator.CallResetPagination();
    }

    /**
     * Formats the numeric values in the totalizers object using Brazilian Portuguese number formatting.
     *
     * @param {GeneralObject} totalizers - An object containing commission-related numeric values
     * @returns {object} An object with the same structure as the input but with values formatted as strings
     * with two decimal places using Brazilian Portuguese number format (e.g., "1.234,56")
     * @property {string} commissionAmount - Formatted commission amount
     * @property {string} totalAmountReceivable - Formatted total amount receivable
     * @property {string} totalAvailableRedemption - Formatted total available for redemption
     */
    function adaptTotalizersValues(totalizers: GeneralObject) {
      return {
        commissionAmount: formatToBrCurrency(totalizers.commissionAmount),
        totalAmountReceivable: formatToBrCurrency(totalizers.totalAmountReceivable),
        totalAvailableRedemption: formatToBrCurrency(totalizers.totalAvailableRedemption),
      };
    }

    function $reset() {
      clearCommission();
      clearCommissionList();
      clearCommissionListFilters();
      clearCommissionTotalizers();
    }

    async function dispatchListCommissions(): Promise<void> {
      toggleCommissionListLoading();

      try {
        const result: MySsApiResponseDataList = await API.listCommissions(
          AUTH_STORE.session.currentEconomicGroupMember,
        );
        if (result.success) {
          commissionListPaginator.CallSetPagination(result.data.pagination);
          setCommissionList(AdaptSalesCommissionListItem.toModelList(result.data.items));
        }
      } catch (error) {
        setCommissionList([]);
      }
    }

    async function dispatchLoadMoreResults(): Promise<void> {
      toggleCommissionListLoading();

      try {
        const result: MySsApiResponseDataList = await API.listCommissions(
          AUTH_STORE.session.currentEconomicGroupMember,
          {
            page: commissionListPaginator.pagination.value.page + 1,
            pageSize: commissionListPaginator.pagination.value.pageSize,
          },
        );

        if (result.success) {
          commissionListPaginator.CallSetPagination(result.data.pagination);
          updateCommissionList(
            AdaptSalesCommissionListItem.toModelList(result.data.items),
          );
        }
      } catch (error) {
        setCommissionList([]);
      }
    }

    async function dispatchShowCommission(salesCommissionId: string): Promise<void> {
      toggleCommissionLoading();
      clearCommission();

      await API.showCommission(
        AUTH_STORE.session.currentEconomicGroupMember,
        salesCommissionId,
      ).then(
        ({ data }) => {
          setCommission(AdaptSalesCommission.toModel(data));
          toggleCommissionLoading();
        },
        (error) => {
          toggleCommissionLoading();
          throw error;
        },
      );
    }

    async function dispatchFilterCommissions(): Promise<void> {
      toggleCommissionListLoading();
      const params = AdaptSalesCommissionFilters.toOutputDTO(commissionListFilters.value);

      try {
        const result: MySsApiResponseDataList = await API.listCommissions(
          AUTH_STORE.session.currentEconomicGroupMember,
          params,
        );

        if (result.success) {
          commissionListPaginator.CallSetPagination(result.data.pagination);
          setCommissionList(AdaptSalesCommissionListItem.toModelList(result.data.items));
        }
      } catch (error) {
        setCommissionList();
      }
    }

    async function dispatchGetCommissions(force = false): Promise<void> {
      if (profile.value.isCommercial) return;
      if (!listTimer.isExpired.value && !force) return;
      if (force) clearCommissionListFilters();

      toggleCommissionListLoading();

      await API.listCommissions(
        AUTH_STORE.session.currentEconomicGroupMember,
        AdaptSalesCommissionFilters.toOutputDTO(commissionListFilters.value),
      ).then(
        (response) => {
          const { items, pagination } = response.data;
          setCommissionList(AdaptSalesCommissionListItem.toModelList(items));
          commissionListPaginator.CallSetPagination(pagination);
          listTimer.startTimer();
          toggleCommissionListLoading();
        },
        (error) => {
          setCommissionList();
          commissionListPaginator.CallResetPagination();
          toggleCommissionListLoading();
          throw error;
        },
      );
    }

    async function dispatchGetCommissionTotalizers(force = false): Promise<void> {
      if (profile.value.isCommercial) return;
      if (!totalizerTimer.isExpired.value && !force) return;

      toggleTotalizerLoading();
      await listCommissionsTotalizers(AUTH_STORE.session.currentEconomicGroupMember).then(
        ({ data }) => {
          setCommissionTotalizers(adaptTotalizersValues(data));
          totalizerTimer.startTimer();
          toggleTotalizerLoading();
        },
        (error) => {
          toggleTotalizerLoading();
          throw error;
        },
      );
    }

    async function onChangeEconomicGroup(): Promise<void> {
      Promise.all([dispatchGetCommissions(true), dispatchGetCommissionTotalizers(true)]);
    }

    async function onSessionOpen(): Promise<void> {
      Promise.all([dispatchGetCommissions(), dispatchGetCommissionTotalizers()]);
    }

    return {
      commission: getCommission,
      commissionList: getCommissionList,
      commissionListLoading: getCommissionListLoading,
      commissionLoading: getCommissionLoading,
      commissionListFilters: getCommissionListFilters,
      commissionTotalizers: getCommissionTotalizers,
      setCommission,
      updateCommission,
      clearCommission,
      setCommissionList,
      updateCommissionList,
      clearCommissionList,
      setCommissionListFilters,
      updateCommissionListFilters,
      clearCommissionListFilters,
      toggleCommissionListLoading,
      commissionListPagination: commissionListPaginator.pagination,
      CallCommissionListPaginate: commissionListPaginator.CallPaginate,
      dispatchListCommissions,
      dispatchShowCommission,
      dispatchLoadMoreResults,
      dispatchFilterCommissions,
      dispatchGetCommissionTotalizers,
      dispatchGetCommissions,
      onSessionOpen,
      onSessionClose: $reset,
      onChangeEconomicGroup,
    };
  },
  { persist: true },
);
